import { spaceClient } from './client';

// Game Announcement + Prize Grant — all calls scoped to the active space via spaceClient
// (see gameScoreBoardApi.js for the same convention: spaceClient prefixes API_ROOT/{spaceId}).
//
// Base path per announcement: /games/{gameId}/announcements — matches
// "api/space/{spaceId}/games/{gameId}/announcements" once spaceClient prepends
// API_ROOT (= API_BASE + /api/space) + /{spaceId}.

// ── Announcements ────────────────────────────────────────────────────────────
// type: 'weekly' | 'monthly' | 'custom'. startDate/endDate only sent when type === 'custom'
// — for weekly/monthly, referenceDate (any date inside the desired week/month) is sent
// instead; the server still computes the actual boundaries either way, referenceDate just
// picks WHICH week/month (defaults server-side to "now" if omitted).
// Quiz: gameScheduleIds (the quizzes it is for, one or more) replace the type, the reference date and the dates — the
// server computes the type and the start/end (earliest quiz start .. latest quiz end) itself, so none of them is sent.
const periodFields = ({ gameScheduleIds, announcementType, startDate, endDate, referenceDate }) => {
  if (gameScheduleIds?.length) return { gameScheduleIds };
  return { announcementType, ...(announcementType === 'custom' ? { startDate, endDate } : { referenceDate }) };
};

export const createAnnouncement = (gameId, {
  name, announcementType, startDate, endDate, referenceDate, gameScheduleIds, topCount, announcementDate, prizePackItems,
}) =>
  spaceClient
    .post(`/games/${gameId}/announcements`, {
      name,
      topCount,
      announcementDate: announcementDate || null,
      prizePackItems: prizePackItems ?? [],
      ...periodFields({ gameScheduleIds, announcementType, startDate, endDate, referenceDate }),
    })
    .then((r) => r.data);

// Quiz only: the approved quizzes of the space that are not in another announcement. While editing, pass the
// announcement's id so the quizzes it already has stay in the list.
export const getAvailableSchedules = (gameId, excludeAnnouncementId = null) =>
  spaceClient
    .get(`/games/${gameId}/announcements/available-schedules`, {
      params: excludeAnnouncementId ? { excludeAnnouncementId } : undefined,
    })
    .then((r) => r.data);

export const getAnnouncements = (gameId) =>
  spaceClient.get(`/games/${gameId}/announcements`).then((r) => r.data);

export const getAnnouncementById = (gameId, announcementId) =>
  spaceClient.get(`/games/${gameId}/announcements/${announcementId}`).then((r) => r.data);

export const updateAnnouncement = (gameId, announcementId, {
  name, announcementType, startDate, endDate, referenceDate, gameScheduleIds, topCount, announcementDate, status, prizePackItems,
}) =>
  spaceClient
    .put(`/games/${gameId}/announcements/${announcementId}`, {
      name,
      topCount,
      announcementDate: announcementDate || null,
      status,
      prizePackItems: prizePackItems ?? [],
      ...periodFields({ gameScheduleIds, announcementType, startDate, endDate, referenceDate }),
    })
    .then((r) => r.data);

export const deleteAnnouncement = (gameId, announcementId) =>
  spaceClient.delete(`/games/${gameId}/announcements/${announcementId}`).then((r) => r.data);

// ── Eligible players ─────────────────────────────────────────────────────────
// Same response shape as gameScoreBoardApi.getScoreboard: { items, totalCount, pageNumber,
// pageSize, fromDate, toDate } — rendered with ScoreboardTable directly.
export const getEligiblePlayers = (gameId, announcementId, { page = 1, pageSize = 25 } = {}) =>
  spaceClient
    .get(`/games/${gameId}/announcements/${announcementId}/eligible-players`, {
      params: { page, pageSize },
    })
    .then((r) => r.data);

// Quiz only: one row per selected quiz the player played — [{ gameScheduleId, name, startDate, endDate, points,
// correctCount, totalQuestions, playedAt }]. Replaces the date-based player history for a Quiz announcement.
export const getAnnouncementPlayerBreakdown = (gameId, announcementId, userId) =>
  spaceClient
    .get(`/games/${gameId}/announcements/${announcementId}/players/${userId}/breakdown`)
    .then((r) => r.data);

// ── Grant ────────────────────────────────────────────────────────────────────
// Prize assignment is staged client-side only (AnnouncementDetailModal) — there is no
// standalone assign/remove/list-prizes endpoint anymore. `assignments` (any number of
// { userId, itemId, quantity } pairs the admin picked in the wizard, a plain array — a single
// assignment is just a one-element array) is sent as part of this one grant call, which
// persists them AND grants in a single request/transaction server-side.
export const grantPrizes = (gameId, announcementId, userIds, assignments = []) =>
  spaceClient
    .post(`/games/${gameId}/announcements/${announcementId}/grant`, { userIds, assignments })
    .then((r) => r.data);

export const getWinners = (gameId, announcementId) =>
  spaceClient.get(`/games/${gameId}/announcements/${announcementId}/winners`).then((r) => r.data);

// ── Prize item catalog (space-scoped, not game-scoped) ──────────────────────
// [{ itemId, itemTypeId, nameKey, descriptionKey, isAvailable }]
// Route is singular "item" (matches ItemController's own [Route("api/space/{spaceId}/item")]),
// not "items" — this was originally written against the wrong path and 404'd every call.
export const getPrizeCatalog = () =>
  spaceClient.get('/item/prize-only').then((r) => r.data);

// ── Prize pack — fallback item(s) granted together as a bundle to any winner ──
// with no per-user row in game_announcement_user_prize — set at create/edit time via
// createAnnouncement/updateAnnouncement's prizePackItems.
export const getPrizePackItems = (gameId, announcementId) =>
  spaceClient.get(`/games/${gameId}/announcements/${announcementId}/prize-pack`).then((r) => r.data);
